/* Hallmark · component: FileUpload · genre: modern-minimal · theme: maeiz-tokens
 * Drag-and-drop file upload with preview, file size validation, and theme integration
 */
import React, { useState, useRef, useEffect } from "react";
import { UploadCloud, File as FileIcon, X, AlertCircle } from "lucide-react";
import { clsx } from "clsx";

export interface FileUploadProps {
    id?: string;
    label?: string;
    helperText?: string;
    error?: string;
    value?: File | null;
    onChange?: (file: File | null) => void;
    accept?: string;
    maxSizeMB?: number;
    className?: string;
    disabled?: boolean;
    "data-testid"?: string;
}

function formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export const FileUpload: React.FC<FileUploadProps> = ({
    id,
    label,
    helperText,
    error: propError,
    value,
    onChange,
    accept = "image/jpeg,image/png,image/gif",
    maxSizeMB = 5,
    className,
    disabled = false,
    "data-testid": testId,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [localError, setLocalError] = useState<string | null>(null);

    const inputId = id ?? `file-upload-${Math.random().toString(36).slice(2, 9)}`;

    useEffect(() => {
        if (!value) {
            setPreviewUrl(null);
            return;
        }

        if (value.type.startsWith("image/")) {
            const url = URL.createObjectURL(value);
            setPreviewUrl(url);
            return () => {
                URL.revokeObjectURL(url);
            };
        } else {
            setPreviewUrl(null);
        }
    }, [value]);

    const validateAndSet = (file: File | null) => {
        setLocalError(null);
        if (!file) {
            onChange?.(null);
            return;
        }

        const maxBytes = maxSizeMB * 1024 * 1024;
        if (file.size > maxBytes) {
            setLocalError(`File size exceeds ${maxSizeMB} MB limit.`);
            return;
        }

        onChange?.(file);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (disabled) return;

        const droppedFile = e.dataTransfer.files?.[0] ?? null;
        if (droppedFile) {
            validateAndSet(droppedFile);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0] ?? null;
        validateAndSet(selectedFile);
    };

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (inputRef.current) inputRef.current.value = "";
        validateAndSet(null);
    };

    const activeError = propError || localError;

    return (
        <div className={clsx("w-full space-y-1.5", className)}>
            {label && (
                <label
                    htmlFor={inputId}
                    className="block text-sm font-medium text-neutral-dark dark:text-white"
                >
                    {label}
                </label>
            )}

            <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !disabled && inputRef.current?.click()}
                className={clsx(
                    "relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center transition-all duration-200 cursor-pointer",
                    isDragging
                        ? "border-gold bg-gold/10 scale-[1.01]"
                        : activeError
                          ? "border-danger/70 bg-danger/5 hover:border-danger"
                          : "border-neutral-border/80 dark:border-white/15 bg-white/40 dark:bg-white/[0.02] hover:border-gold/60 hover:bg-gold/[0.02]",
                    disabled && "cursor-not-allowed opacity-60"
                )}
            >
                <input
                    ref={inputRef}
                    id={inputId}
                    type="file"
                    accept={accept}
                    disabled={disabled}
                    onChange={handleFileChange}
                    className="sr-only"
                    data-testid={testId}
                />

                {value ? (
                    <div className="flex w-full items-center justify-between gap-4">
                        <div className="flex items-center gap-3.5 min-w-0">
                            {previewUrl ? (
                                <img
                                    src={previewUrl}
                                    alt={value.name}
                                    className="h-14 w-14 rounded-xl object-cover border border-neutral-border/60 dark:border-white/15 shadow-sm shrink-0"
                                />
                            ) : (
                                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gold/15 text-gold border border-gold/30">
                                    <FileIcon size={24} />
                                </div>
                            )}
                            <div className="text-start truncate">
                                <p className="text-sm font-semibold text-neutral-dark dark:text-white truncate">
                                    {value.name}
                                </p>
                                <p className="text-xs text-neutral-dark/60 dark:text-neutral-light/60 font-mono mt-0.5">
                                    {formatFileSize(value.size)}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                type="button"
                                onClick={handleRemove}
                                className="rounded-lg p-2 text-neutral-dark/50 dark:text-neutral-light/50 hover:bg-danger/10 hover:text-danger transition-colors"
                                title="Remove file"
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-2">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gold/10 text-gold border border-gold/25">
                            <UploadCloud size={24} />
                        </div>
                        <div className="space-y-0.5">
                            <p className="text-sm font-semibold text-neutral-dark dark:text-white">
                                <span className="text-gold hover:underline">Click to upload</span>{" "}
                                <span className="font-normal text-neutral-dark/70 dark:text-neutral-light/70">
                                    or drag and drop
                                </span>
                            </p>
                            <p className="text-xs text-neutral-dark/50 dark:text-neutral-light/50">
                                {helperText ?? `JPG, PNG or GIF (max. ${maxSizeMB}MB)`}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {activeError && (
                <div className="flex items-center gap-1.5 text-xs text-danger">
                    <AlertCircle size={14} className="shrink-0" />
                    <span>{activeError}</span>
                </div>
            )}
        </div>
    );
};
